import { createContext, useContext, useEffect, useState, ReactNode, useRef, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { Loader2 } from 'lucide-react';
import type { Database } from '../types/database';
import { toast } from 'sonner';

type Profile = Database['public']['Tables']['profiles']['Row'];

interface AuthState {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  session: Session | null;
  isApprover: boolean;
}

export interface AuthContextType extends AuthState {
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  updateProfileLocally: (updates: Partial<Profile>) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Add a utility for consistent logging
const logWithTime = (message: string, data?: any) => {
  console.log(`[${new Date().toISOString()}] ${message}`, data ? data : '');
};

// Add session attempt tracking
let refreshAttemptId = 0;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    profile: null,
    loading: true,
    session: null,
    isApprover: false,
  });

  // Add ref to track ongoing profile fetches
  const profileFetchRef = useRef<{ [key: string]: boolean }>({});
  // Ref to signal manual login initiation reliably
  const manualLoginInitiatedRef = useRef(false);

  // Add function to handle localStorage profile caching
  const getProfileFromCache = (userId: string): Profile | null => {
    try {
      const cachedProfile = localStorage.getItem(`profile_${userId}`);
      if (cachedProfile) {
        const profileData = JSON.parse(cachedProfile);
        const cacheTime = localStorage.getItem(`profile_${userId}_time`);

        // Check if cache is less than 4 hours old
        if (cacheTime && (Date.now() - parseInt(cacheTime, 10)) < 4 * 60 * 60 * 1000) {
          logWithTime(`[Auth] Using cached profile for user`, { userId });
          return profileData;
        }
      }
    } catch (e) {
      logWithTime(`[Auth] Error reading profile from cache`, { userId, error: e });
    }
    return null;
  };

  const saveProfileToCache = (userId: string, profile: Profile) => {
    try {
      localStorage.setItem(`profile_${userId}`, JSON.stringify(profile));
      localStorage.setItem(`profile_${userId}_time`, Date.now().toString());
      logWithTime(`[Auth] Saved profile to cache`, { userId });
    } catch (e) {
      logWithTime(`[Auth] Error saving profile to cache`, { userId, error: e });
    }
  };

  async function updateAuthState(user: User | null, session: Session | null) {
    const updateId = Date.now().toString();
    let isUserApprover = false;

    try {
      if (!user) {
        logWithTime(`[Auth State Update ${updateId}] No user, clearing state`);
        setState({ user: null, profile: null, loading: false, session: null, isApprover: false });
        return;
      }

      logWithTime(`[Auth State Update ${updateId}] Starting profile fetch for user`, { userId: user.id });
      profileFetchRef.current[user.id] = true;

      // --- First try to get profile from cache ---
      const cachedProfile = getProfileFromCache(user.id);
      if (cachedProfile) {
        // If we have cached profile, use it and fetch the latest version in the background
        logWithTime(`[Auth State Update ${updateId}] Using cached profile while fetching latest`, { userId: user.id });

        // Set state with cached profile immediately
        isUserApprover = await checkApproverStatus(user.id, updateId);
        setState({
          user,
          profile: cachedProfile,
          loading: false,
          session,
          isApprover: isUserApprover
        });

        // Continue with fetch in background (don't await)
        fetchLatestProfile(user, session, updateId);
        return;
      }

      // --- Profile Fetch ---
      let profile: Profile | null = null;
      let profileError: any = null;
      try {
        logWithTime(`[Auth State Update ${updateId}] Preparing profile query`, { userId: user.id });

        // Use direct table query instead of potentially problematic RPC function
        const profilePromise = supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();

        logWithTime(`[Auth State Update ${updateId}] Using direct table query for profile`, { userId: user.id });

        const result = await Promise.race([
          profilePromise,
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Profile fetch timeout')), 5000) // Much shorter timeout - 5 seconds
          )
        ]) as { data: any, error: any };

        // Handle result from direct table query
        if (result.data) {
          profile = result.data as Profile;
        } else {
          profile = null;
        }
        profileError = result.error;

        if (profileError) {
          logWithTime(`[Auth State Update ${updateId}] Profile table query error`, {
            error: profileError instanceof Error ? profileError.message : profileError,
            userId: user.id,
            errorObject: profileError
          });
        } else if (profile) {
          logWithTime(`[Auth State Update ${updateId}] Profile table query success`, {
            userId: user.id,
            role: profile.role,
            hasEmail: !!profile.email,
            hasName: !!profile.name
          });

          // Cache the profile
          saveProfileToCache(user.id, profile);
        } else {
          logWithTime(`[Auth State Update ${updateId}] Profile table query returned no data`, { userId: user.id });
        }

        logWithTime(`[Auth State Update ${updateId}] Profile fetch completed`, { userId: user.id, hasProfile: !!profile, hasError: !!profileError });

      } catch (fetchCatchError) {
        logWithTime(`[Auth State Update ${updateId}] Profile fetch EXCEPTION CAUGHT`, { userId: user.id, error: fetchCatchError });
        profileError = fetchCatchError; // Assign caught error
      }
      // --- End Profile Fetch ---

      delete profileFetchRef.current[user.id]; // Clean up ref
      if (profileError) {
        // Log the specific profile error *before* returning
        logWithTime(`[Auth State Update ${updateId}] Profile fetch FAILED`, { error: profileError instanceof Error ? profileError.message : profileError, userId: user.id });
        // Keep user and session, just clear profile and isApprover
        setState(prev => ({
          ...prev,
          user, // Keep user
          session, // Keep session
          loading: false,
          profile: null, // Set profile to null
          isApprover: false // Reset approver status
        }));
        return;
      }
      if (profile) {
        logWithTime(`[Auth State Update ${updateId}] Profile found, checking approver status`, { userId: user.id, profileRole: profile.role });
        isUserApprover = await checkApproverStatus(user.id, updateId);
      } else {
        logWithTime(`[Auth State Update ${updateId}] No profile found, cannot check approver status`, { userId: user.id });
      }

      logWithTime(`[Auth State Update ${updateId}] Setting final state`, {
        userId: user.id,
        role: profile?.role,
        isApprover: isUserApprover,
        hasSession: !!session
      });

      setState({
        user,
        profile,
        loading: false,
        session,
        isApprover: isUserApprover
      });
    } catch (error) {
      // This is the final catch block
      if (user) delete profileFetchRef.current[user.id];
      // Log the detailed error object here
      logWithTime(`[Auth State Update ${updateId}] MAIN CATCH BLOCK ERROR`, {
        error, // Log the full error object 
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        userId: user?.id
      });
      // Still clear the state on unexpected errors
      setState(prev => ({ ...prev, loading: false, user: null, profile: null, session: null, isApprover: false }));
    }
  }

  // Helper function to check approver status
  async function checkApproverStatus(userId: string, updateId: string): Promise<boolean> {
    try {
      // Reverted to direct table query, using supabaseClient instance
      const { count, error: approverError } = await supabase
        .from('payment_approvers')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_active', true);

      if (approverError) {
        logWithTime(`[Auth State Update ${updateId}] Approver check FAILED`, { error: approverError.message, userId });
        return false;
      } else {
        const isApprover = (count ?? 0) > 0;
        logWithTime(`[Auth State Update ${updateId}] Approver status checked`, { isApprover, userId });
        return isApprover;
      }
    } catch (error) {
      logWithTime(`[Auth State Update ${updateId}] Approver check EXCEPTION`, { error, userId });
      return false;
    }
  }

  // Function to fetch latest profile in the background, not blocking UI
  async function fetchLatestProfile(user: User, session: Session | null, updateId: string) {
    try {
      logWithTime(`[Background Fetch ${updateId}] Starting profile fetch for user`, { userId: user.id });

      const { data, error: profileError } = await supabase
        .rpc('get_auth_profile', { p_user_id: user.id });

      // Handle JSON result - the RPC function returns JSON, not an array
      const profile = data ? data : null;

      if (profileError) {
        logWithTime(`[Background Fetch ${updateId}] Error fetching latest profile`, {
          error: profileError,
          userId: user.id
        });
        return; // Keep using the cached profile
      }

      // Cast the RPC result to Profile type
      const profileData = profile as unknown as Profile;

      if (profileData) {
        // Update the cache with the latest data
        saveProfileToCache(user.id, profileData);

        // Check if the role has changed, which might require UI updates
        const cachedProfile = getProfileFromCache(user.id);
        if (cachedProfile?.role !== profileData.role) {
          logWithTime(`[Background Fetch ${updateId}] Profile role changed, updating state`, {
            userId: user.id,
            oldRole: cachedProfile?.role,
            newRole: profileData.role
          });

          // Update state with new profile data
          const isUserApprover = await checkApproverStatus(user.id, updateId);
          setState(prev => {
            // Only update if user is still the same (they haven't logged out during the fetch)
            if (prev.user?.id === user.id) {
              return {
                ...prev,
                profile: profileData,
                isApprover: isUserApprover
              };
            }
            return prev;
          });
        }
      }
    } catch (error) {
      logWithTime(`[Background Fetch ${updateId}] Exception fetching latest profile`, {
        error,
        userId: user.id
      });
      // Don't update state on error, keep using cached profile
    }
  }

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      logWithTime('[Auth Initialize] Starting');
      refreshAttemptId++;
      const currentAttemptId = refreshAttemptId;
      try {
        // Add overall timeout for initialization
        const initPromise = (async () => {
          // Use supabaseClient instance
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();

          if (!mounted) return;

          if (sessionError) {
            logWithTime('[Auth Initialize] Error', { error: sessionError.message });
            setState(prev => ({ ...prev, loading: false }));
            return;
          }

          if (session?.user) {
            logWithTime('[Auth Initialize] Session found', {
              userId: session.user.id,
              expiresAt: session.expires_at
            });
            await updateAuthState(session.user, session);
          } else {
            logWithTime('[Auth Initialize] No session');
            setState(prev => ({ ...prev, loading: false }));
          }
        })();

        // Race with timeout to prevent infinite loading
        await Promise.race([
          initPromise,
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Auth initialization timeout')), 8000) // 8 second timeout
          )
        ]);

        // Use supabaseClient instance
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            logWithTime('[Auth State Change]', { event, userId: session?.user?.id });

            if (!mounted) {
              logWithTime('[Auth State Change] Unmounted, ignoring.');
              return;
            }

            try {
              switch (event) {
                case 'SIGNED_IN':
                case 'TOKEN_REFRESHED':
                case 'USER_UPDATED':
                  if (session?.user) {
                    const isFetchInProgress = profileFetchRef.current[session.user.id];

                    if (isFetchInProgress) {
                      logWithTime('[Auth State Change] Profile fetch already in progress, skipping full update for now.', { event });
                    } else {
                      logWithTime('[Auth State Change] No profile fetch in progress, running full update.', { event });
                      await updateAuthState(session.user, session);
                    }

                    // --- Redirect Logic (Checks Ref) ---
                    const wasManualLogin = manualLoginInitiatedRef.current;

                    // Check if this is a password recovery session - if so, don't redirect
                    const isPasswordRecovery = window.location.pathname === '/reset-password' ||
                      window.location.pathname.includes('/reset-password') ||
                      window.location.hash.includes('type=recovery') ||
                      window.location.search.includes('type=recovery') ||
                      window.location.hash.includes('access_token') ||
                      window.location.search.includes('access_token');

                    if (wasManualLogin && event === 'SIGNED_IN' && !isPasswordRecovery) {
                      logWithTime('[Auth State Change] Manual login detected via Ref, initiating redirect');
                      manualLoginInitiatedRef.current = false; // Reset the flag immediately

                      // Fetch profile role *only* for redirect if not already available in state
                      // Use state.profile first if available and user matches
                      let userRole = (state.user?.id === session.user.id) ? state.profile?.role : null;

                      if (!userRole) {
                        logWithTime('[Auth State Change] Profile role not in state, fetching for redirect...');
                        // Use RPC for consistency if preferred, or direct table query
                        const { data: profileForRedirect, error: profileError } = await supabase
                          .from('profiles')
                          .select('role')
                          .eq('user_id', session.user.id)
                          .single();

                        if (profileError) {
                          logWithTime('[Auth State Change] Error fetching profile for redirect', { error: profileError });
                          window.location.href = '/'; // Redirect to a safe default
                          return;
                        }
                        userRole = profileForRedirect?.role;
                      } else {
                        logWithTime('[Auth State Change] Using profile role from state for redirect.', { role: userRole });
                      }

                      let targetUrl = '/'; // Default redirect
                      if (userRole === 'CupShup') {
                        targetUrl = '/cupshup';
                      } else if (userRole === 'Vendor') {
                        targetUrl = '/vendor';
                      } else if (userRole === 'Client') {
                        targetUrl = '/client';
                      } else {
                        logWithTime('[Auth State Change] Unknown role for redirect.', { role: userRole });
                      }

                      logWithTime('[Auth State Change] Redirecting now.', { targetUrl });
                      window.location.href = targetUrl; // Use href for full page reload
                      // --- End Redirect Logic ---
                    } else if (isPasswordRecovery) {
                      logWithTime('[Auth State Change] Password recovery detected, skipping redirect');
                      manualLoginInitiatedRef.current = false; // Reset flag to prevent future redirects
                    }
                  } else {
                    logWithTime('[Auth State Change] No user/session on event, clearing state.', { event });
                    setState({ user: null, profile: null, loading: false, session: null, isApprover: false });
                  }
                  break;
                case 'SIGNED_OUT':
                  logWithTime('[Auth State Change] Signed out, clearing state.');
                  manualLoginInitiatedRef.current = false; // Ensure flag is reset on sign out
                  setState({ user: null, profile: null, loading: false, session: null, isApprover: false });
                  break;
                case 'PASSWORD_RECOVERY':
                  logWithTime('[Auth State Change] Password recovery event detected.');
                  // Don't redirect or update state - let the ResetPassword component handle this
                  break;
                default:
                  logWithTime('[Auth State Change] Unhandled event type.', { event });
              }
            } catch (listenerError) {
              logWithTime('[Auth State Change] LISTENER ERROR CAUGHT', { error: listenerError, errorMessage: listenerError instanceof Error ? listenerError.message : 'Unknown listener error', event, userId: session?.user?.id });
              manualLoginInitiatedRef.current = false; // Reset flag on error too
              setState({ user: null, profile: null, loading: false, session: null, isApprover: false });
            }
          }
        );

        return () => {
          logWithTime('[Auth Cleanup] Unsubscribing from auth state changes.');
          subscription.unsubscribe();
        };
      } catch (error) {
        logWithTime('[Auth Initialize] Error', {
          error: error instanceof Error ? error.message : 'Unknown error'
        });
        if (mounted) {
          setState(prev => ({ ...prev, loading: false }));
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
    };
  }, []);

  // Memoize the context value
  const contextValue = useMemo<AuthContextType>(() => ({
    user: state.user,
    profile: state.profile,
    loading: state.loading,
    isLoading: state.loading,
    session: state.session,
    isApprover: state.isApprover,
    signIn: async (email: string, password: string) => {
      // Set the flag *before* initiating the Supabase call
      manualLoginInitiatedRef.current = true;
      logWithTime('[SignIn] Manual login initiated, setting Ref flag.');
      try {
        // DO NOT set global loading state here, let the component handle it
        // setState(prev => ({ ...prev, loading: true })); 

        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          // Reset flag if Supabase call fails
          manualLoginInitiatedRef.current = false;
          // setState(prev => ({ ...prev, loading: false })); 
          throw error;
        }

        // NO reset of flag here on success - the event handler will do it.
        logWithTime('[SignIn] Supabase auth successful, waiting for SIGNED_IN event handler to redirect');

        // Keep loading state active - event handler manages the final state/redirect
        return;
      } catch (error) {
        // Reset flag if any error occurs during the process
        manualLoginInitiatedRef.current = false;
        // setState(prev => ({ ...prev, loading: false }));
        console.error('Error during signIn call:', error);
        throw error;
      }
    },
    signOut: async () => {
      logWithTime('[Sign Out] Function called!', { stack: new Error('SignOut Trace').stack });
      manualLoginInitiatedRef.current = false; // Reset flag on sign out intent
      try {
        setState(prev => ({ ...prev, loading: true }));
        const { error } = await supabase.auth.signOut();
        if (error) {
          logWithTime('[Sign Out] Error', { error: error.message });
          // Do NOT reset loading here if error, let finally/event handler do it
          throw error;
        }
        logWithTime('[Sign Out] Success');
        // State cleared by 'SIGNED_OUT' event, which also resets the ref
      } catch (error) {
        logWithTime('[Sign Out] Error Catch', { error: error instanceof Error ? error.message : 'Unknown error' });
        toast.error("Logout failed. Please try again.");
        // Reset loading state if sign out fails completely
        setState(prev => ({ ...prev, loading: false }));
      }
      // No finally block needed as SIGNED_OUT event handles state clearing
    },
    refreshProfile: async () => {
      if (!state.user) return;
      await updateAuthState(state.user, state.session);
    },
    updateProfileLocally: (updates: Partial<Profile>) => {
      setState(prev => {
        if (!prev.user || !prev.profile) return prev;
        const updatedProfile = { ...prev.profile, ...updates } as Profile;
        saveProfileToCache(prev.user.id, updatedProfile);
        return { ...prev, profile: updatedProfile };
      });
    },
  }), [state.user, state.profile, state.loading, state.session, state.isApprover]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}